import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Output, PLATFORM_ID,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { OnlineProduct } from '../../../shared/interfaces/product';
import { BlockHeaderGroup } from '../../../shared/interfaces/block-header-group';
import { DirectionService } from '../../../shared/services/direction.service';
import { AnalyticsList, AnalyticsService } from '../../../shared/services/analytics.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'app-block-products-carousel',
    templateUrl: './block-products-carousel.component.html',
    styleUrls: ['./block-products-carousel.component.scss']
})
export class BlockProductsCarouselComponent implements OnChanges, AfterViewInit {
    @Input() header: string;
    @Input() layout: 'grid-4'|'grid-4-sm'|'grid-5'|'horizontal' = 'grid-4';
    @Input() rows = 1;
    @Input() products: OnlineProduct[] = [];
    @Input() groups: BlockHeaderGroup[] = [];
    @Input() withSidebar = false;
    @Input() loading = false;
    @Input() hideHeader = false;
    @Input() hideBrand = false;

    @Output() groupChange: EventEmitter<BlockHeaderGroup> = new EventEmitter();

    @ViewChild('container', {read: ElementRef}) container: ElementRef;

    columns: OnlineProduct[][] = [];

    /// This carousel as a GA4 product list, named after its header.
    list: AnalyticsList = AnalyticsService.listFor(null);

    showCarousel = true;

    carouselDefaultOptions: any = {
        items: 4,
        margin: 14,
        nav: false,
        dots: false,
        loop: true, 
        autoplay:true,
        autoplayTimeout:8000,
        autoplayHoverPause:true,
        stagePadding: 1,
        rtl: this.direction.isRTL()
    };

    carouselOptionsByLayout: any = {
        'grid-4': {
            responsive: {
                0:    {items: 2},
                320:  {items: 2, margin: 0},
                400:  {items: 2, margin: 10},
                690:  {items: 3, margin: 10},
                930:  {items: 4, margin: 10},
                1110: {items: 4, margin: 14} 
            }
        },
        'grid-4-sm': {
            responsive: {
                820: {items: 4, margin: 14},
                640: {items: 3, margin: 10},
                400: {items: 2, margin: 10},
                0:   {items: 1}
            }
        },
        'grid-5': {
            responsive: {
                
                0:    {items: 1},
                320:  {items: 1, margin: 0}, 
                400:  {items: 2, margin: 10},
                690:  {items: 3, margin: 10},
                930:  {items: 4, margin: 10}, 
                1110: {items: 5, margin: 12}
            }
        },
        horizontal: {
            items: 3,
            responsive: {
                1110: {items: 3, margin: 14},
                930:  {items: 3, margin: 10},
                690:  {items: 2, margin: 10},
                0:    {items: 1}
            }
        }
    };

    // Built ONCE per layout and handed to owl by the same reference every time. owl-carousel-o
    // compares the options input by reference in ngOnChanges and, on any change, tears the
    // carousel down and re-initialises every slide. A getter that returned a fresh object did
    // that on EVERY change detection pass - each click, scroll and timer rebuilt every carousel
    // on the page, which is what made the browser crawl and hang after the page had loaded.
    private builtOptions: any = null;
    private builtForLayout: string = null;
    get carouselOptions(): any {
        if (this.builtOptions && this.builtForLayout === this.layout) {
            return this.builtOptions;
        }
        const options = Object.assign({}, this.carouselDefaultOptions, this.carouselOptionsByLayout[this.layout]);

        // Server-side render: no clones and no timers. `loop` makes owl render every slide
        // TWICE - the loop clones - which doubled the product cards in every page the server
        // produced, for an animation that never runs there. The browser rebuilds the carousel
        // with the full options, so what the visitor sees is unchanged.
        if (!isPlatformBrowser(this.platformId)) {
            options.loop = false;
            options.autoplay = false;
        }

        this.builtForLayout = this.layout;
        this.builtOptions = options;
        return options;
    }

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private direction: DirectionService,
        private analytics: AnalyticsService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        const properties = Object.keys(changes);

        if (properties.includes('header')) {
            this.list = this.analytics.listNamed(this.header);
        }

        if (properties.includes('products')) {
            // A tab change swaps the products, and that is a new impression of the list.
            this.analytics.viewItemList(this.products, this.list);
        }

        if (properties.includes('products') || properties.includes('row')) {
            this.columns = [];

            if (this.products && this.rows > 0) {
                const products = this.products.slice();

                while (products.length > 0) {
                    this.columns.push(products.splice(0, this.rows));
                }
            }
        }
    }

    ngAfterViewInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            const container = this.container.nativeElement as HTMLElement;
            const containerWidth = container.getBoundingClientRect().width;

            window.addEventListener('load',() => {
                const newContainerWidth = container.getBoundingClientRect().width;

                if (containerWidth !== newContainerWidth) {
                    this.showCarousel = false; 
                    setTimeout(() => this.showCarousel = true, 1);
                }
            }, { passive: true });
        }
    }
}
